import { keyframes } from "@emotion/react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import type { MsComponentPropsWithRef } from "@theme/types/global";
import type { FC } from "react";

export const animations = {
  popoverIn: keyframes({
    from: { opacity: 0 },
    to: { opacity: 1 },
  }),
  popoverOut: keyframes({
    from: { opacity: 1 },
    to: { opacity: 0 },
  }),
  popoverInFromTop: keyframes({
    from: { opacity: 0, transform: "translateY(-8px) scale(0.95)" },
    to: { opacity: 1, transform: "translateY(0) scale(1)" },
  }),
  popoverInFromBottom: keyframes({
    from: { opacity: 0, transform: "translateY(8px) scale(0.95)" },
    to: { opacity: 1, transform: "translateY(0) scale(1)" },
  }),
  popoverInFromLeft: keyframes({
    from: { opacity: 0, transform: "translateX(-8px) scale(0.95)" },
    to: { opacity: 1, transform: "translateX(0) scale(1)" },
  }),
  popoverInFromRight: keyframes({
    from: { opacity: 0, transform: "translateX(8px) scale(0.95)" },
    to: { opacity: 1, transform: "translateX(0) scale(1)" },
  }),
};

const Popover: FC<MsComponentPropsWithRef<typeof PopoverPrimitive.Root>> = ({ ...props }) => (
  <PopoverPrimitive.Root data-slot="popover" {...props} />
);

const PopoverTrigger: FC<MsComponentPropsWithRef<typeof PopoverPrimitive.Trigger>> = ({
  ...props
}) => <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;

const PopoverContent: FC<MsComponentPropsWithRef<typeof PopoverPrimitive.Content>> = ({
  align = "center",
  sideOffset = 4,
  sx,
  ...props
}) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      data-slot="popover-content"
      align={align}
      sideOffset={sideOffset}
      sx={{
        transformOrigin: "var(--radix-popover-content-transform-origin)",
        animationDuration: "150ms",
        animationTimingFunction: "ease-out",
        willChange: "transform, opacity",
        '&[data-state="open"]': {
          animationName: `${animations.popoverIn}`,
        },
        '&[data-state="closed"]': {
          animationName: `${animations.popoverOut}`,
        },
        '&[data-state="open"][data-side="bottom"]': {
          animationName: `${animations.popoverInFromTop}`,
        },
        '&[data-state="open"][data-side="top"]': {
          animationName: `${animations.popoverInFromBottom}`,
        },
        '&[data-state="open"][data-side="left"]': {
          animationName: `${animations.popoverInFromRight}`,
        },
        '&[data-state="open"][data-side="right"]': {
          animationName: `${animations.popoverInFromLeft}`,
        },
        ...sx,
      }}
      {...props}
    />
  </PopoverPrimitive.Portal>
);

const PopoverAnchor: FC<MsComponentPropsWithRef<typeof PopoverPrimitive.Anchor>> = ({
  ...props
}) => <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
